import React, { useState } from 'react';
import { normalizeInputType } from '../../../utils/featureNormalization';
import { CONTROL_DATA_SLOTS, fieldsForSlot, isOutputFeature, normalizeValueSchema } from '../../../utils/valuePresentation';
import { INPUT_TYPE_METADATA } from '../utils/constants';
import TemplateValueField from '../../../components/TemplateValueField';

/**
 * ElementCard component for GuiBuilder
 * Renders an individual element card in the editor
 */
export default function ElementCard({
    el,
    index,
    isFirst,
    isLast,
    features,
    presentationOverrides = {},
    updatePresentationOverride = () => {},
    catalogContent = { features: {} },
    updateCatalogValue = () => {},
    moveElement,
    deleteElement,
    updateElementField
}) {
    const inputTypeOptions = Object.keys(INPUT_TYPE_METADATA);
    const [valuesOpen, setValuesOpen] = useState(false);

    return (
        <div key={el.id} style={{
            display: 'flex',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '1rem',
            gap: '1rem',
            alignItems: 'flex-start',
            position: 'relative'
        }}>
            {/* Reorder Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', justifyContent: 'center' }}>
                <button 
                    type="button"
                    onClick={() => moveElement(index, -1)}
                    disabled={isFirst}
                    style={{ background: 'none', border: 'none', cursor: isFirst ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)', opacity: isFirst ? 0.3 : 1 }}
                >
                    <i className="fa-solid fa-chevron-up" style={{ fontSize: '0.8rem' }}></i>
                </button>
                <button 
                    type="button"
                    onClick={() => moveElement(index, 1)}
                    disabled={isLast}
                    style={{ background: 'none', border: 'none', cursor: isLast ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)', opacity: isLast ? 0.3 : 1 }}
                >
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: '0.8rem' }}></i>
                </button>
            </div>

            {/* Element Configuration Fields */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {el.type === 'header' && (
                    <div>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Section Title Header</span>
                        <input 
                            type="text" 
                            className="input-field" 
                            value={el.text}
                            onChange={e => updateElementField(el.id, 'text', e.target.value)}
                            style={{ margin: 0, padding: '0.5rem', fontSize: '0.9rem', marginTop: '0.25rem' }}
                        />
                    </div>
                )}

                {el.type === 'description' && (
                    <div>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Context Description Text</span>
                        <textarea 
                            className="textarea-field" 
                            value={el.text}
                            onChange={e => updateElementField(el.id, 'text', e.target.value)}
                            style={{ minHeight: '60px', margin: 0, padding: '0.5rem', fontSize: '0.85rem', marginTop: '0.25rem' }}
                        />
                    </div>
                )}

                {(el.type === 'variable' || el.type === 'feature') && (
                    <div>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Product Feature Input</span>
                        <div style={{
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            background: 'var(--bg-primary)', 
                            padding: '0.5rem 0.75rem', 
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            marginTop: '0.25rem'
                        }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                {(el.featureName || el.variableName)}
                                {(() => {
                                    const featureInfo = features.find(v => v.name === (el.featureName || el.variableName));
                                    const isOutput = isOutputFeature(featureInfo);
                                    const friendlyLabel = featureInfo?.displayText;
                                    const defaultRenderType = normalizeInputType(featureInfo?.inputType || el.inputType);
                                    const effectiveRenderType = normalizeInputType(el.renderType || defaultRenderType);
                                    return (
                                        <>
                                            {friendlyLabel && <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: '0.5rem' }}>({friendlyLabel})</span>}
                                            <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '0.1rem 0.35rem', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', borderRadius: '4px', fontWeight: 600 }}>{effectiveRenderType}</span>
                                            {isOutput && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '0.1rem 0.35rem', background: 'rgba(220,38,38,0.1)', color: 'var(--error)', borderRadius: '4px', fontWeight: 600 }}>Output</span>}
                                        </>
                                    );
                                })()}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                {/* Optional check box */}
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={el.isOptional} 
                                        onChange={e => updateElementField(el.id, 'isOptional', e.target.checked)} 
                                    />
                                    Optional Spec
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', fontWeight: 600 }}>
                                    Render as
                                    <select
                                        value={el.renderType || ''}
                                        onChange={e => updateElementField(el.id, 'renderType', e.target.value)}
                                        style={{
                                            padding: '0.25rem 0.45rem',
                                            border: '1px solid var(--border)',
                                            borderRadius: '4px',
                                            background: 'var(--bg-primary)',
                                            color: 'var(--text-primary)',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {(() => {
                                            const featureInfo = features.find(v => v.name === (el.featureName || el.variableName));
                                            const defaultRenderType = normalizeInputType(featureInfo?.inputType || el.inputType);
                                            return <option value="">Default ({defaultRenderType})</option>;
                                        })()}
                                        {inputTypeOptions.map(type => (
                                            <option key={type} value={type}>{INPUT_TYPE_METADATA[type].label}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                        {(() => {
                            const featureName = el.featureName || el.variableName;
                            const featureInfo = features.find(feature => feature.name === featureName);
                            const featureOverride = presentationOverrides[featureName] || {};
                            const renderType = normalizeInputType(el.renderType || featureInfo?.inputType || el.inputType);
                            const slots = CONTROL_DATA_SLOTS[renderType] || [];
                            const schema = normalizeValueSchema(featureInfo?.valueSchema || featureInfo?.property?.valueSchema || []);
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.35rem' }}>
                                    <input
                                        className="input-field"
                                        placeholder="Label override"
                                        value={featureOverride.label || ''}
                                        onChange={event => updatePresentationOverride(featureName, { label: event.target.value })}
                                        style={{ margin: 0, padding: '0.35rem 0.5rem', fontSize: '0.8rem' }}
                                    />
                                    {slots.map(slot => {
                                        const options = fieldsForSlot(schema, slot);
                                        if (options.length < 2) return null;
                                        return (
                                            <label key={slot.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                                {slot.label}
                                                <select
                                                    className="input-field"
                                                    value={el.dataBindings?.[slot.id] || ''}
                                                    onChange={event => updateElementField(el.id, 'dataBindings', {
                                                        ...(el.dataBindings || {}),
                                                        [slot.id]: event.target.value
                                                    })}
                                                    style={{ margin: 0, padding: '0.3rem 0.45rem', fontSize: '0.75rem' }}
                                                >
                                                    <option value="">Choose a template field</option>
                                                    {options.map(field => (
                                                        <option key={field.key} value={field.key}>{field.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    })}
                                    <FeatureValuesFoldout
                                        open={valuesOpen}
                                        onToggle={() => setValuesOpen(current => !current)}
                                        featureName={featureName}
                                        featureInfo={featureInfo}
                                        catalogFeature={catalogContent?.features?.[featureName]}
                                        schema={schema}
                                        updateCatalogValue={updateCatalogValue}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                )}

                {el.type === 'exit-button' && (
                    <div>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Exit Action Button</span>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                            <input 
                                type="text" 
                                className="input-field" 
                                value={el.text}
                                onChange={e => updateElementField(el.id, 'text', e.target.value)}
                                placeholder="Exit Button Label"
                                style={{ margin: 0, padding: '0.5rem', fontSize: '0.9rem' }}
                            />
                            <span style={{ alignSelf: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontStyle: 'italic' }}>Close embed</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Button */}
            <button 
                type="button"
                onClick={() => deleteElement(el.id)}
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    padding: '4px',
                    alignSelf: 'center'
                }}
                title="Delete element"
            >
                <i className="fa-solid fa-trash-can" style={{ color: 'var(--text-secondary)' }}></i>
            </button>
        </div>
    );
}

function FeatureValuesFoldout({ open, onToggle, featureName, featureInfo, catalogFeature, schema, updateCatalogValue }) {
    const values = featureInfo?.values || [];
    const catalogValues = catalogFeature?.values || {};

    const updateValue = (valueKey, patch, exclusiveDefault = false) => {
        updateCatalogValue(featureName, valueKey, patch, exclusiveDefault);
    };

    return (
        <div style={{ marginTop: '0.25rem' }}>
            <button
                type="button"
                onClick={onToggle}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600
                }}
            >
                <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: '0.65rem' }}></i>
                Values ({values.length})
            </button>
            {open && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginTop: '0.45rem' }}>
                    {values.length === 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>This feature has no values.</span>
                    )}
                    {values.map(value => {
                        const key = String(value.value ?? '');
                        const entry = catalogValues[key] || {};
                        const propertyValues = entry.propertyValues || value.propertyValues || {};
                        return (
                            <div key={value.id || key} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '0.55rem 0.65rem', display: 'flex', flexDirection: 'column', gap: '0.45rem', background: 'var(--bg-primary)' }}>
                                <strong style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{key}</strong>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                    Label
                                    <input
                                        className="input-field"
                                        value={entry.label ?? value.displayText ?? ''}
                                        onChange={event => updateValue(key, { label: event.target.value })}
                                        style={{ margin: 0, padding: '0.3rem 0.45rem', fontSize: '0.75rem' }}
                                    />
                                </label>
                                <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(entry.isDefault ?? value.isDefault)}
                                        onChange={event => updateValue(key, { isDefault: event.target.checked }, true)}
                                    />
                                    Default starting value
                                </label>
                                {schema.map(field => (
                                    <TemplateValueField
                                        key={field.key}
                                        field={field}
                                        value={propertyValues[field.key]}
                                        onChange={next => updateValue(key, {
                                            propertyValues: { ...propertyValues, [field.key]: next }
                                        })}
                                    />
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
